import React, { useState } from 'react';
import { leaguesAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, Upload, FileSpreadsheet, Info, Eye } from 'lucide-react';
import { ImportPreviewTable } from '@/components/ImportPreviewTable';

export const LeagueImportPanel = ({ leagueId }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [templateType, setTemplateType] = useState('all');
  const [importMode, setImportMode] = useState('all');

  const handleDownloadTemplate = async () => {
    try {
      const res = await leaguesAPI.downloadImportTemplate(leagueId, templateType);
      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leaguehub_import_template_${leagueId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore nel download del template');
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error('Seleziona un file .xlsx, .xls o .csv');
      return;
    }
    const fileExt = selectedFile.name.split('.').pop()?.toLowerCase();
    if (fileExt === 'csv' && importMode !== 'members' && importMode !== 'all') {
      toast.error('Il CSV supporta solo l\'import dei membri');
      return;
    }
    setLoading(true);
    setResult(null);
    setPreviewData(null);
    try {
      const res = await leaguesAPI.previewImport(leagueId, selectedFile, importMode);
      setPreviewData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella lettura del file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async (editedData) => {
    setConfirming(true);
    try {
      const payload = {
        mode: editedData.mode,
        preview: editedData.preview,
      };
      const res = await leaguesAPI.confirmImport(leagueId, payload);
      setResult(res.data);
      setPreviewData(null);
      setSelectedFile(null);
      toast.success('Import completato con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'import');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
  };

  // Se siamo in modalità preview, mostra la tabella editabile
  if (previewData) {
    return (
      <Card>
        <CardContent className="pt-6">
          <ImportPreviewTable
            previewData={previewData}
            onConfirm={handleConfirmImport}
            onCancel={handleCancelPreview}
            confirming={confirming}
          />

          {result?.summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Utenti creati</p>
                <p className="text-lg font-semibold">{result.summary.users_created}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Membri creati</p>
                <p className="text-lg font-semibold">{result.summary.members_created}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Classifica aggiornata</p>
                <p className="text-lg font-semibold">{result.summary.stats_updated}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Partite importate</p>
                <p className="text-lg font-semibold">{result.summary.matches_created}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Importazione dati
        </CardTitle>
        <CardDescription>
          Carica un file Excel per importare membri, classifica e partite della lega.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Formato consigliato</AlertTitle>
          <AlertDescription>
            Usa i template dedicati per evitare errori. Formati supportati: .xlsx, .xls, .csv.
            Dopo il caricamento vedrai un'anteprima dei dati per verificarli prima dell'import.
          </AlertDescription>
        </Alert>

        <div className="grid gap-3 lg:grid-cols-[220px_1fr]">
          <Select value={templateType} onValueChange={setTemplateType}>
            <SelectTrigger>
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Template completo</SelectItem>
              <SelectItem value="members">Template membri</SelectItem>
              <SelectItem value="stats">Template classifica</SelectItem>
              <SelectItem value="matches">Template partite</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-2" />
            Scarica template
          </Button>
        </div>

        <div className="grid gap-3 lg:grid-cols-[220px_1fr_auto]">
          <Select value={importMode} onValueChange={setImportMode}>
            <SelectTrigger>
              <SelectValue placeholder="Cosa importare" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Importa tutto</SelectItem>
              <SelectItem value="members">Solo membri</SelectItem>
              <SelectItem value="stats">Solo classifica</SelectItem>
              <SelectItem value="matches">Solo partite</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              setSelectedFile(e.target.files?.[0] || null);
              setResult(null);
              setPreviewData(null);
            }}
          />
          <Button onClick={handlePreview} disabled={loading || !selectedFile}>
            <Eye className="h-4 w-4 mr-2" />
            {loading ? 'Caricamento...' : 'Carica e anteprima'}
          </Button>
        </div>

        {selectedFile && (
          <p className="text-sm text-muted-foreground">
            File selezionato: <span className="font-medium">{selectedFile.name}</span>
          </p>
        )}

        {result?.summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Utenti creati</p>
              <p className="text-lg font-semibold">{result.summary.users_created}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Membri creati</p>
              <p className="text-lg font-semibold">{result.summary.members_created}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Classifica aggiornata</p>
              <p className="text-lg font-semibold">{result.summary.stats_updated}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Partite importate</p>
              <p className="text-lg font-semibold">{result.summary.matches_created}</p>
            </div>
          </div>
        )}

        {result?.errors?.length > 0 && (
          <Alert variant="destructive">
            <AlertTitle>Import con errori</AlertTitle>
            <AlertDescription>
              {result.errors.map((err, index) => (
                <p key={`${err}-${index}`}>{err}</p>
              ))}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default LeagueImportPanel;
